import classNames from "classnames";
import {
    ChildGridCarousel,
    ChildGridCarouselBlock,
} from "components/ChildGridCarousel";
import Clickable from "components/Clickable";
import {
    Dropdown,
    DropdownItem,
    DropdownProps,
} from "components/Dropdown/Dropdown";
import { FeaturedGridFilters } from "components/FeaturedGridFilters";
import { GridFilters } from "components/GridFilters";
import { GridFilterTags } from "components/GridFilters/GridFilterTags";
import type { Filter } from "components/GridFilters/index";
import { GridList } from "components/GridList";
import { SlimHero } from "components/HeroBlocks/SlimHero";
import Layout from "components/Layout";
import { PageNavBar } from "components/PageNavBar";
import { ProductGrid } from "components/ProductGrid";
import { AlignedRichText, AlignedRichTextBlock } from "components/RichText";
import Spacer, { SpacerBlock } from "components/Spacer";
import { readCMSDataClientSide, readCMSDataServerSide } from "helpers/api";
import { useQuery } from "helpers/query";
import type { DRFPaginatedQuery, PageFragment } from "interfaces";
import type { ProductGridTile } from "interfaces/products";
import type { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";
import type {
    EmtekPageProps,
    InferGetWagtailServerSidePropsType,
} from "pages/[[...slug]]";
import CaretIcon from "public/svg/caret.svg";
import { EmtekClientProvider } from "queryClient";
import { useRef, useState } from "react";
import { Key } from "react-aria-components";

import { BlockWidthProvider } from "./SplitLayoutSubPage";

import styles from "./grid-page.module.scss";

export type GridPageData = EmtekPageProps<{
    subtitle?: string;
    header_blocks: Array<GridPageBlock>;
    child_grids: PageFragment[];
    sibling_grids: PageFragment[];
    filters: Filter[];
    below_grid_blocks: BelowGridBlock[];
}>;

type GridPageBlock = ChildGridCarouselBlock;

type BelowGridBlock = AlignedRichTextBlock | SpacerBlock;

type Props = InferGetWagtailServerSidePropsType<typeof fetchData>;

export default function GridPageWrapper(props: Props) {
    return (
        <EmtekClientProvider>
            <GridPage {...props} />
        </EmtekClientProvider>
    );
}

function GridPage({ wagtail, menus, tiles: initialTiles, ordering }: Props) {
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const router = useRouter();
    const { data: tiles, isLoading: gridIsLoading } = useQuery<
        DRFPaginatedQuery<ProductGridTile>
    >({
        namespaceID: menus[0].site,
        queryKey: ["tiles", wagtail.id, router.query],
        queryFn: () => {
            return readCMSDataClientSide(`/api/gridtiles/${wagtail.id}`, {
                search: new URLSearchParams(window.location.search),
                cache: "no-store",
            });
        },
        initialData: initialTiles,
        staleTime: 0,
        cacheTime: 0,
        refetchOnMount: true,
    });

    console.log("*****************CLIENTSIDE:******************");
    console.log(tiles);
    console.log("*****************CLIENTSIDE:******************");

    if (!tiles)
        throw new Error(
            "Tile data should always be present, either from initial data or aditional fetched data.",
        );

    const gridRef = useRef<HTMLElement>(null);
    const hasFocusFilters =
        wagtail.filters.filter((filter) => {
            return filter.is_featured;
        }).length > 0;

    const countFilters = () => {
        let queryKeys = [...Object.entries(router.query)];
        queryKeys = queryKeys.filter(
            (entry) =>
                entry[0] !== "slug" &&
                entry[0] !== "ordering" &&
                entry[0] !== "offset" &&
                entry[0] !== "limit",
        );

        let numQueries = 0;
        queryKeys.map((query) => {
            if (Array.isArray(query[1])) {
                numQueries += query[1].length;
            } else {
                numQueries++;
            }
        });

        if (numQueries > 0) {
            return `(${numQueries})`;
        }

        return "";
    };

    const subtitle = !!wagtail.subtitle ? `<p>${wagtail.subtitle}</p>` : "";
    const hasBelowGridBlocks = wagtail.below_grid_blocks.length > 0;

    return (
        <Layout menus={menus} metadata={wagtail}>
            <main>
                <SlimHero
                    className={
                        wagtail.header_blocks[0]?.type === "child_grid_carousel"
                            ? styles.overlapHeading
                            : ""
                    }
                    cmsData={{
                        content: `
              <h1>${wagtail.title}</h1>
              ${subtitle}
            `,
                        media: { image: null, video: null },
                        background_color: "pewter1",
                        text_color: "auto",
                    }}
                />
                {wagtail.header_blocks.map((block) => {
                    switch (block.type) {
                        case "child_grid_carousel":
                            return (
                                <ChildGridCarousel
                                    key={block.id}
                                    {...block.value}
                                />
                            );
                    }
                })}
                {hasFocusFilters && (
                    <FeaturedGridFilters
                        filters={wagtail.filters}
                        nonzeroFilterIDs={tiles.remaining_attrs}
                    />
                )}
                <div className={styles.gridPage}>
                    <aside>
                        <GridList wagtail={wagtail} />
                        <GridFilters
                            filters={wagtail.filters}
                            nonzeroFilterIDs={tiles.remaining_attrs}
                        />
                    </aside>
                    <section className={styles.mobileListFilters}>
                        <div className={styles.browseByDropdown}>
                            <Dropdown
                                label="Browse by"
                                labelSize="small"
                                buttonContent={wagtail.title}
                            >
                                {wagtail.ancestors.map(
                                    (page: PageFragment, index) => {
                                        if (page.slug === "/") return null;
                                        return (
                                            <DropdownItem
                                                key={page.id}
                                                href={page.slug}
                                                className={classNames({
                                                    [styles[
                                                        `browseByBold${index}`
                                                    ]]: true,
                                                    [styles.browseByCurrent]:
                                                        page.id === wagtail.id,
                                                })}
                                            >
                                                {page.title}
                                            </DropdownItem>
                                        );
                                    },
                                )}
                                {wagtail.child_grids.length > 0 &&
                                    wagtail.child_grids.map(
                                        (page: PageFragment) => (
                                            <DropdownItem
                                                key={page.id}
                                                href={page.slug}
                                                className={
                                                    styles[
                                                        `browseBy${wagtail.ancestors.length}`
                                                    ]
                                                }
                                            >
                                                {page.title}
                                            </DropdownItem>
                                        ),
                                    )}
                            </Dropdown>
                        </div>
                        <div className={styles.filtersSort}>
                            <Clickable
                                unstyled={true}
                                className={styles.filterCount}
                                onClick={() => setShowMobileFilters(true)}
                            >
                                {`Filters ${countFilters()}`}
                                <CaretIcon className={styles.caretIcon} />
                            </Clickable>
                            <div>
                                <SortDropdown
                                    label="Sort By"
                                    labelVisible={false}
                                    className={styles.mobileSort}
                                />
                            </div>
                        </div>
                    </section>
                    <section
                        className={hasBelowGridBlocks ? styles.padBottom : ""}
                        ref={gridRef}
                    >
                        <div className={styles.desktopGridTopBar}>
                            <span>{tiles.count} Products</span>
                            <SortDropdown
                                className={`${styles.desktopSort} al-grid__sort`}
                                label="Sort by:"
                            />
                        </div>
                        <GridFilterTags filters={wagtail.filters} />
                        <ProductGrid
                            tiles={tiles.results}
                            loading={gridIsLoading}
                        />
                        <PageNavBar
                            {...tiles}
                            padBottom={!hasBelowGridBlocks}
                        />
                        <BlockWidthProvider value="full">
                            {wagtail.below_grid_blocks.map((block) => {
                                switch (block.type) {
                                    case "aligned_rich_text":
                                        return (
                                            <AlignedRichText
                                                key={block.id}
                                                {...block.value}
                                            />
                                        );
                                    case "spacer":
                                        return (
                                            <Spacer
                                                key={block.id}
                                                {...block.value}
                                            />
                                        );
                                }
                            })}
                        </BlockWidthProvider>
                    </section>
                </div>
            </main>
            <div className={styles.mobileFilters} hidden={!showMobileFilters}>
                <GridFilters
                    filters={wagtail.filters}
                    isMobile={true}
                    setShowMobileFilters={setShowMobileFilters}
                />
            </div>
        </Layout>
    );
}

export async function fetchData(
    context: GetServerSidePropsContext,
    wagtailPageProps: GridPageData,
    apiResponse: Response,
) {
    const query = new URL(
        context.resolvedUrl,
        `https://${context.req.headers.host}`,
    ).searchParams;
    const tiles = (await readCMSDataServerSide(
        `/api/gridtiles/${wagtailPageProps.wagtail.id}`,
        {
            context: context,
            search: query,
        },
    )) as DRFPaginatedQuery<ProductGridTile>;

    const ordering = query.get("ordering") as "title" | "-title" | null;

    return {
        props: {
            ...wagtailPageProps,
            tiles,
            ordering,
        },
        apiResponse: apiResponse,
    };
}

export function SortDropdown(props: Partial<Omit<DropdownProps, "value">>) {
    const [value, setValue] = useState("default" as Key);

    const router = useRouter();
    return (
        <Dropdown
            onChange={(newValue) => {
                // setting `router.query.ordering` to undefined does not remove the query param from the url, it just sets it to empty - need to use delete
                if (newValue === "default") delete router.query.ordering;
                else router.query.ordering = newValue.toString();
                // Reset offset so you're back on page 1
                delete router.query.offset;
                router.replace({ query: router.query }, undefined, {
                    scroll: false,
                    shallow: true,
                });
                setValue(newValue);
            }}
            value={value?.toString()}
            {...props}
        >
            <DropdownItem id="default">Featured</DropdownItem>
            <DropdownItem id="title">A-Z</DropdownItem>
            <DropdownItem id="-title">Z-A</DropdownItem>
        </Dropdown>
    );
}
